/**
 * Note the deliberate asymmetry in `badgeCount`, preserved from the original
 * inline config: All reads its unread count unconditionally, while Mentions /
 * Participating / Unattended fall back to 0 unless `hasFilteredUnreadCounts`
 * is on. Do not "tidy" this.
 *
 * Participating is a low-traffic queue, so it sits behind the "More" fold at
 * the end of the group. The fold state lives in UI settings (see
 * `useSidebarNavigation`), which is why the builder receives it resolved.
 *
 * Counts here render as the one neutral chip every sidebar row uses - see
 * SidebarUnreadBadge. Red is reserved for the two places that must interrupt,
 * neither of which is a row: the notification bell and the rail's Inbox badge.
 * The header carries the same total the All row does, because the group folds
 * and a folded group would otherwise hide the signal entirely.
 */
export const CONVERSATIONS_UNREAD_GETTER =
  'conversationUnreadCounts/getAllUnreadCount';

export default ({
  t,
  accountScopedRoute,
  hasFilteredUnreadCounts,
  allUnreadCount,
  mentionsUnreadCount,
  participatingUnreadCount,
  unattendedUnreadCount,
  showConversationsMore,
  toggleConversationsMore,
}) => ({
  name: 'Conversation',
  label: t('SIDEBAR.CONVERSATIONS'),
  icon: 'i-ph-chat-circle',
  getterKeys: { count: CONVERSATIONS_UNREAD_GETTER },
  children: [
    {
      name: 'All',
      label: t('SIDEBAR.ALL_CONVERSATIONS'),
      icon: 'i-ph-tray',
      badgeCount: allUnreadCount,
      activeOn: ['inbox_conversation'],
      to: accountScopedRoute('home'),
    },
    // Each scoped queue is a route of its own, so the path alone identifies it
    // and no query matching is needed to keep exactly one row lit. `activeOn`
    // holds the queue open while one of its conversations is on screen.
    {
      name: 'For you',
      label: t('SIDEBAR.FOR_YOU'),
      icon: 'i-ph-user',
      to: accountScopedRoute('conversation_mine'),
      activeOn: ['conversation_mine_conversation'],
    },
    {
      name: 'Unassigned',
      label: t('SIDEBAR.UNASSIGNED_CONVERSATIONS'),
      icon: 'i-ph-user-circle-dashed',
      to: accountScopedRoute('conversation_unassigned'),
      activeOn: ['conversation_unassigned_conversation'],
    },
    {
      name: 'Mentions',
      label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
      icon: 'i-ph-at',
      badgeCount: hasFilteredUnreadCounts ? mentionsUnreadCount : 0,
      activeOn: ['conversation_through_mentions'],
      to: accountScopedRoute('conversation_mentions'),
      folded: !showConversationsMore,
    },
    {
      name: 'Unattended',
      activeOn: ['conversation_through_unattended'],
      label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
      icon: 'i-ph-clock-countdown',
      badgeCount: hasFilteredUnreadCounts ? unattendedUnreadCount : 0,
      to: accountScopedRoute('conversation_unattended'),
      folded: !showConversationsMore,
    },
    {
      name: 'Participating',
      label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
      icon: 'i-ph-user-check',
      badgeCount: hasFilteredUnreadCounts ? participatingUnreadCount : 0,
      activeOn: ['conversation_through_participating'],
      to: accountScopedRoute('conversation_participating'),
      // Always present so the collapsed rail and active-route matching can
      // reach it; the expanded list hides it while the More fold is closed.
      folded: !showConversationsMore,
    },
    {
      name: 'More',
      label: showConversationsMore
        ? t('SIDEBAR.SHOW_LESS')
        : t('SIDEBAR.SHOW_MORE'),
      icon: showConversationsMore ? 'i-ph-caret-up' : 'i-ph-caret-down',
      onSelect: toggleConversationsMore,
    },
  ],
});
